/**
 * component-video-stories.js
 *
 * Substituto nativo do app "Reelfy Videos" (vídeos shoppable em formato stories).
 * Feito com JS puro, sem dependências, seguindo o princípio: no máximo 1 <video>
 * montado e decodificando no DOM a qualquer momento. Isso é proposital: parte do
 * diagnóstico de travamento no Android apontou para excesso de decodificação de
 * vídeo simultânea (apps + seções autoplay). Aqui, o vídeo só é criado quando a
 * story está ativa, e é destruído (removido do DOM) ao trocar de slide.
 *
 * <video-stories-launcher> — dispara a abertura do viewer a partir de uma bolha
 *   flutuante ou de uma miniatura em carrossel/grade.
 * <video-story-viewer>     — o overlay em tela cheia, com progresso, navegação
 *   por toque/clique/teclado e troca de vídeo sob demanda.
 *
 * Camadas extra de segurança contra o travamento em Android (relato de crash no
 * navegador interno do Instagram, provavelmente por pressão de memória somada
 * ao WebView do Linktree que ainda não foi totalmente descartado):
 * 1. Autoplay de miniaturas nunca acontece em telas <768px, mesmo com a
 *    configuração "Autoplay nas miniaturas" ligada — nessas telas, sempre exige
 *    toque explícito antes de decodificar qualquer vídeo em segundo plano.
 *    (Vale apenas para carrossel/grade, onde há várias miniaturas na tela. O
 *    widget flutuante toca normalmente no mobile: é um único vídeo, e no modo
 *    bolinha ocupa poucos pixels.)
 * 2. O widget flutuante inteiro é suprimido (não é criado) quando o navegador é
 *    identificado como o WebView interno do Instagram/Facebook — é o cenário
 *    exato onde o crash foi observado. Nada muda para os demais visitantes.
 */

const VIDEO_STORIES_MOBILE_QUERY = '(max-width: 768px)';

function isNarrowViewport() {
  return window.matchMedia(VIDEO_STORIES_MOBILE_QUERY).matches;
}

function isInstagramOrFacebookInAppBrowser() {
  const ua = navigator.userAgent || '';
  return /Instagram|FBAN|FBAV/i.test(ua);
}


class VideoStoriesLauncher extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const trigger = event.target.closest('[data-story-open]');
    if (!trigger) return;

    event.preventDefault();
    const targetId = this.getAttribute('data-story-target');
    const viewer = targetId ? document.getElementById(targetId) : null;
    if (!viewer) return;

    const index = Number(trigger.getAttribute('data-story-open')) || 0;
    viewer.open(index);
  };
}

class VideoStoryViewer extends HTMLElement {
  #activeIndex = 0;
  #slides = [];
  #mountedVideo = null;
  #mountedSlide = null;
  #touchStartX = null;
  #previouslyFocused = null;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-story-slide]'));

    this.addEventListener('click', this.#onStageClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: true });
  }

  disconnectedCallback() {
    this.#unmountCurrentVideo();
  }

  open(index = 0) {
    if (this.#slides.length === 0) return;
    this.#previouslyFocused = document.activeElement;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('video-story-viewer-open');
    document.querySelectorAll('video-floating-widget').forEach((el) => el.pauseForViewer?.());
    this.#goTo(index);
    this.focus({ preventScroll: true });
  }

  close = () => {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('video-story-viewer-open');
    this.#unmountCurrentVideo();
    document.querySelectorAll('video-floating-widget').forEach((el) => el.resumeAfterViewer?.());
    if (this.#previouslyFocused && typeof this.#previouslyFocused.focus === 'function') {
      this.#previouslyFocused.focus({ preventScroll: true });
    }
  };

  next = () => {
    if (this.#activeIndex >= this.#slides.length - 1) {
      this.close();
      return;
    }
    this.#goTo(this.#activeIndex + 1);
  };

  prev = () => {
    if (this.#activeIndex <= 0) return;
    this.#goTo(this.#activeIndex - 1);
  };

  #goTo(index) {
    this.#unmountCurrentVideo();

    this.#slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
    });

    this.#activeIndex = index;
    this.#updateProgress(index);

    const slide = this.#slides[index];
    if (!slide) return;

    this.#mountVideo(slide);
  }

  #mountVideo(slide) {
    const template = slide.querySelector('[data-story-video]');
    const poster = slide.querySelector('[data-story-poster]');
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');
    if (!video) return;

    video.setAttribute('playsinline', '');
    // preload 'auto': o usuário já pediu para ver este vídeo, então buscar os
    // dados imediatamente (o valor vindo do Liquid é 'none', bom para não gastar
    // banda antes do clique, ruim depois dele).
    video.setAttribute('preload', 'auto');
    video.muted = false;
    video.addEventListener('ended', this.next, { once: true });
    video.addEventListener('timeupdate', this.#onTimeUpdate);
    video.addEventListener('loadedmetadata', this.#onTimeUpdate);

    // A capa só sai quando houver imagem de verdade para mostrar. Esconder antes
    // disso deixava a tela preta enquanto o vídeo carregava — ou para sempre,
    // caso o navegador bloqueasse a reprodução.
    video.addEventListener(
      'playing',
      () => {
        if (poster) poster.hidden = true;
      },
      { once: true }
    );

    slide.appendChild(video);

    // A capa fica com loading="lazy" enquanto o player está fechado (não gasta
    // banda em toda visita). Ao abrir, promove para carregamento imediato.
    if (poster) {
      poster.hidden = false;
      poster.loading = 'eager';
      poster.fetchPriority = 'high';
    }

    this.#mountedVideo = video;
    this.#mountedSlide = slide;

    const showManualControls = () => {
      video.controls = true;
      if (poster) poster.hidden = true;
    };

    video.addEventListener('error', showManualControls, { once: true });

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // Reprodução com áudio bloqueada pelo navegador: tenta silenciado.
        video.muted = true;
        video.play().catch(showManualControls);
      });
    }
  }

  #unmountCurrentVideo() {
    if (!this.#mountedVideo) return;

    const video = this.#mountedVideo;
    const slide = this.#mountedSlide;

    video.removeEventListener('timeupdate', this.#onTimeUpdate);
    video.pause();
    video.removeAttribute('src');
    video.load();
    video.remove();

    if (slide) {
      const poster = slide.querySelector('[data-story-poster]');
      if (poster) poster.hidden = false;
    }

    this.#mountedVideo = null;
    this.#mountedSlide = null;
  }

  #onTimeUpdate = () => {
    const video = this.#mountedVideo;
    if (!video || !video.duration) return;
    const progress = this.querySelectorAll('[data-story-progress] > *')[this.#activeIndex];
    if (!progress) return;
    const ratio = Math.min(video.currentTime / video.duration, 1);
    progress.style.setProperty('--story-progress', ratio);
  };

  #updateProgress(activeIndex) {
    const bars = this.querySelectorAll('[data-story-progress] > *');
    bars.forEach((bar, i) => {
      bar.style.setProperty('--story-progress', i < activeIndex ? 1 : 0);
      bar.classList.toggle('is-active', i === activeIndex);
    });
  }

  #onStageClick = (event) => {
    if (event.target.closest('[data-story-close]')) {
      this.close();
      return;
    }
    if (event.target.closest('[data-story-next]')) {
      this.next();
      return;
    }
    if (event.target.closest('[data-story-prev]')) {
      this.prev();
      return;
    }
    // Tocar/clicar no vídeo: terço esquerdo volta, resto avança.
    if (event.target.closest('a, button')) return;
    const stage = this.querySelector('[data-story-slides]');
    if (!stage || !stage.contains(event.target)) return;

    const rect = stage.getBoundingClientRect();
    const clickRatio = (event.clientX - rect.left) / rect.width;
    if (clickRatio < 0.3) {
      this.prev();
    } else {
      this.next();
    }
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape') this.close();
    if (event.key === 'ArrowRight') this.next();
    if (event.key === 'ArrowLeft') this.prev();
  };

  #onTouchStart = (event) => {
    this.#touchStartX = event.changedTouches[0].clientX;
  };

  #onTouchEnd = (event) => {
    if (this.#touchStartX === null) return;
    const deltaX = event.changedTouches[0].clientX - this.#touchStartX;
    this.#touchStartX = null;
    if (Math.abs(deltaX) < 40) return;
    if (deltaX > 0) {
      this.prev();
    } else {
      this.next();
    }
  };
}

/**
 * <video-preview> — usado nas miniaturas do carrossel/grade quando "Autoplay nas
 * miniaturas" está ligado. Só monta o <video> quando o card entra na viewport
 * (IntersectionObserver) e desmonta ao sair — mesma disciplina de "no máximo 1
 * vídeo decodificando por vez" do restante do componente, mesmo com várias
 * miniaturas na tela.
 */
class VideoPreview extends HTMLElement {
  #observer;
  #mounted = false;

  connectedCallback() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.#mount();
          } else {
            this.#unmount();
          }
        });
      },
      { rootMargin: '100px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#unmount();
  }

  #mount() {
    if (this.#mounted) return;
    if (isNarrowViewport()) return;
    const template = this.querySelector('[data-preview-video]');
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');
    if (!video) return;

    video.muted = true;
    video.setAttribute('playsinline', '');
    this.appendChild(video);

    const poster = this.querySelector('[data-preview-poster]');
    if (poster) poster.hidden = true;

    video.play().catch(() => {});
    this.#mounted = true;
  }

  #unmount() {
    if (!this.#mounted) return;
    const video = this.querySelector('video');
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
      video.remove();
    }
    const poster = this.querySelector('[data-preview-poster]');
    if (poster) poster.hidden = false;
    this.#mounted = false;
  }
}

/**
 * <video-floating-widget> — caixa fixa na tela (mesmo sistema de posicionamento
 * do botão do WhatsApp) que toca o vídeo atual (se "autoplay" estiver ligado) ou
 * mostra a capa estática. Clicar na caixa abre o visualizador em tela cheia no
 * slide atual. Fechável (X), lembrando a escolha pelo resto da sessão do
 * navegador via sessionStorage. Pausa sozinho quando a aba fica em segundo
 * plano, e quando o visualizador em tela cheia abre por cima dele.
 */
class VideoFloatingWidget extends HTMLElement {
  #currentIndex = 0;
  #slides = [];
  #mountedVideo = null;
  #dismissKey = '';
  #autoplay = false;

  connectedCallback() {
    if (isInstagramOrFacebookInAppBrowser()) {
      // Cenário exato do relato de crash: WebView interno do Instagram/Facebook,
      // já sob pressão de memória por causa do Linktree. Não cria o widget.
      this.remove();
      return;
    }

    this.#dismissKey = `video-stories-floating-dismissed-${this.id}`;
    if (sessionStorage.getItem(this.#dismissKey) === '1') {
      this.remove();
      return;
    }

    this.#slides = Array.from(this.querySelectorAll('[data-floating-slide]'));
    this.#autoplay = this.dataset.autoplay === 'true';

    this.addEventListener('click', this.#onClick);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    this.#show(0);
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    this.#unmountVideo();
  }

  pauseForViewer = () => {
    this.#unmountVideo();
  };

  resumeAfterViewer = () => {
    if (this.#autoplay) this.#show(this.#currentIndex);
  };

  #onVisibilityChange = () => {
    if (document.hidden) {
      this.#unmountVideo();
    } else if (this.#autoplay) {
      this.#show(this.#currentIndex);
    }
  };

  #onClick = (event) => {
    if (event.target.closest('[data-floating-close]')) {
      this.#dismiss();
      return;
    }
    if (event.target.closest('[data-floating-prev]')) {
      const next = this.#currentIndex - 1 < 0 ? this.#slides.length - 1 : this.#currentIndex - 1;
      this.#show(next);
      return;
    }
    if (event.target.closest('[data-floating-next]')) {
      this.#show((this.#currentIndex + 1) % this.#slides.length);
      return;
    }
    // Qualquer outro clique na caixa abre o visualizador em tela cheia no slide atual.
    const targetId = this.getAttribute('data-story-target');
    const viewer = targetId ? document.getElementById(targetId) : null;
    if (viewer) viewer.open(this.#currentIndex);
  };

  #dismiss() {
    sessionStorage.setItem(this.#dismissKey, '1');
    this.#unmountVideo();
    this.remove();
  }

  #show(index) {
    this.#unmountVideo();
    this.#slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    this.#currentIndex = index;

    if (!this.#autoplay) return;

    const slide = this.#slides[index];
    const template = slide?.querySelector('[data-floating-video]');
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');
    if (!video) return;

    video.muted = true;
    video.setAttribute('playsinline', '');
    if (index === 0) video.setAttribute('fetchpriority', 'high');
    slide.appendChild(video);

    const poster = slide.querySelector('[data-floating-poster]');
    if (poster) poster.hidden = true;

    video.play().catch(() => {});
    this.#mountedVideo = video;
  }

  #unmountVideo() {
    if (!this.#mountedVideo) return;
    this.#mountedVideo.pause();
    this.#mountedVideo.removeAttribute('src');
    this.#mountedVideo.load();
    this.#mountedVideo.remove();
    this.#mountedVideo = null;

    const slide = this.#slides[this.#currentIndex];
    const poster = slide?.querySelector('[data-floating-poster]');
    if (poster) poster.hidden = false;
  }
}

if (!window.customElements.get('video-stories-launcher')) {
  window.customElements.define('video-stories-launcher', VideoStoriesLauncher);
}

if (!window.customElements.get('video-story-viewer')) {
  window.customElements.define('video-story-viewer', VideoStoryViewer);
}

if (!window.customElements.get('video-preview')) {
  window.customElements.define('video-preview', VideoPreview);
}

if (!window.customElements.get('video-floating-widget')) {
  window.customElements.define('video-floating-widget', VideoFloatingWidget);
}
